import {
  createSettingsParams,
  ISettingsParam,
  SettingsParamType,
} from '@wix/tpa-settings';
import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';
import { CategoriesPosition, Structure } from '../../enums';
import constants from '../../constants';
import { isEditor } from '@wix/yoshi-flow-editor';

type CONSTANTS = typeof constants;

export type DisplayContent =
  | boolean
  | CONSTANTS['DISPLAY_CONTENT']['SPLIT_INTO_CATEGORIES']
  | CONSTANTS['DISPLAY_CONTENT']['ONE_LIST_OF_QUESTIONS'];

export type Layout = 'collapse' | 'stripes' | 'basic';
export type TextAlignment =
  | CONSTANTS['ALIGNMENT']['LEFT']
  | CONSTANTS['ALIGNMENT']['RIGHT'];

export type HeightApp =
  | CONSTANTS['HEIGHT_APP']['ADJUST']
  | CONSTANTS['HEIGHT_APP']['FIXED'];
export type Spaces =
  | CONSTANTS['SPACES']['LOOSE']
  | CONSTANTS['SPACES']['TIGHT'];
export enum OpenQuestionIcon {
  ARROW = 'arrow',
  PLUS = 'plus',
  ARROWUP = 'arrowUp',
  MINUS = 'minus',
  PLUSCIRCLE = 'plusCircle',
  ARROWUPRIGHT = 'arrowUpRight',
  EYEICON = 'eyeIcon',
}

export type TitleAlignment =
  | CONSTANTS['ALIGNMENT']['LEFT']
  | CONSTANTS['ALIGNMENT']['RIGHT']
  | CONSTANTS['ALIGNMENT']['CENTER'];
export enum LayoutType {
  Collapse = 'collapse',
  Stripes = 'stripes',
  Basic = 'basic',
}

export type Spacing = {
  gap: number;
  vertical: number;
  horizontal: number;
  betweencols: number;
};

export enum ItemAppearance {
  BOX = 'box',
  LINE = 'line',
  NONE = 'none',
}
export type SocialData = {
  show: boolean;
  facebook: boolean;
  twitter: boolean;
  linkedin: boolean;
  copyQuestionLink: boolean;
};
type IManageCategories = { showAll: boolean; categories: string[] };
export type Title = {
  text?: string;
  show: boolean;
  alignment: TitleAlignment;
};
export type SearchBar = {
  text: string;
  show: boolean;
};
export type ISettingsParams = {
  openFirstAnswer: boolean;
  enableScrollAnimation: boolean;
  textAlignment: TextAlignment;
  heightApp: HeightApp;
  widgetUrl: string;
  shortenUrl: string;
  spaces: Spaces;
  manageCategories: IManageCategories;
  layout: LayoutType;
  displayContent: DisplayContent;
  seo: {
    show: boolean;
  };
  title: Title;
  titleText: string;
  searchBar: SearchBar;
  searchBarText: string;
  social: SocialData;
  openOneAnswer: boolean;
  openQuestionIcon: OpenQuestionIcon;
  dividerCollapse: number;

  mobile: {
    titleFontSize: number;
    categoryFontSize: number;
    questionsNumberFontSize: number;
    questionsFontSize: number;
    answersFontSize: number;
  };
  previewFirstAnswer: boolean;
  categoriesPosition: CategoriesPosition;
  structure: Structure;
  spacing: {
    gap: number;
    vertical: number;
    horizontal: number;
    betweencols: number;
  };
  openQuestionIconURL: string;
  itemAppearance: ItemAppearance;
  isEditor3: boolean;
};

export const getSpacingDefaults = ({
  isMobile,
  getSettingParamValue,
}: {
  isMobile: boolean;
  getSettingParamValue: (
    param: ISettingsParam,
    options?: {
      isMobile?: boolean;
      isRTL?: boolean;
      isEditorX?: boolean;
    },
  ) => any;
}) => {
  const layout = getSettingParamValue(settingsParams.layout);
  const itemAppearance = getSettingParamValue(settingsParams.itemAppearance);
  let gaps = { gap: 12, betweencols: 12 };

  if (!isMobile) {
    if (itemAppearance === ItemAppearance.LINE) {
      gaps = { gap: 24, betweencols: 36 };
    }
  }
  const horizontal =
    isMobile &&
    (layout === LayoutType.Collapse || layout === LayoutType.Stripes)
      ? 0
      : isMobile && layout === LayoutType.Basic
      ? 20
      : 32;

  const vertical = isMobile ? 0 : 32;

  return {
    vertical,
    horizontal,
    ...gaps,
  };
};

const settingsParams = createSettingsParams<ISettingsParams>({
  layout: {
    getDefaultValue: () => LayoutType.Collapse,
    metadata: ({ t }) => ({
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: LayoutType.Collapse,
      },
    }),
  },
  openFirstAnswer: {
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  enableScrollAnimation: {
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  textAlignment: {
    getDefaultValue: () => constants.ALIGNMENT.LEFT,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: constants.ALIGNMENT.LEFT,
      },
    },
  },
  heightApp: {
    getDefaultValue: () => constants.HEIGHT_APP.ADJUST,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: constants.HEIGHT_APP.ADJUST,
      },
    },
  },
  widgetUrl: {
    getDefaultValue: () => '',
    metadata: {
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: '',
      },
    },
  },
  shortenUrl: {
    getDefaultValue: () => '',
    metadata: {
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: '',
      },
    },
  },
  spaces: {
    getDefaultValue: () => constants.SPACES.LOOSE,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: constants.SPACES.LOOSE,
      },
    },
  },
  manageCategories: {
    inheritFromAppScope: true,
    getDefaultValue: () => ({
      showAll: true,
      categories: [],
    }),
    metadata: {
      type: DataType.schema,
      defaultValue: {
        type: 'static',
        value: {
          showAll: true,
          categories: [],
        },
      },
    },
  },
  displayContent: {
    getDefaultValue: () => constants.DISPLAY_CONTENT.SPLIT_INTO_CATEGORIES,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: constants.DISPLAY_CONTENT.SPLIT_INTO_CATEGORIES,
      },
    },
  },
  seo: {
    getDefaultValue: () => ({ show: true }),
    metadata: {
      type: DataType.schema,
      defaultValue: {
        type: 'static',
        value: { show: true },
      },
    },
  },
  title: {
    getDefaultValue: ({ t }) => ({
      text: t('display.tab.title.demo'),
      show: true,
      alignment: constants.ALIGNMENT.LEFT,
    }),
    metadata: ({ t }) => ({
      type: DataType.schema,
      defaultValue: {
        type: 'static',
        value: {
          text: t('display.tab.title.demo'),
          show: true,
          alignment: constants.ALIGNMENT.LEFT,
        },
      },
    }),
  },
  searchBar: {
    getDefaultValue: ({ t }) => ({
      text: t('display.tab.search.bar.demo'),
      show: true,
    }),
    metadata: ({ t }) => ({
      type: DataType.schema,
      defaultValue: {
        type: 'static',
        value: {
          text: t('display.tab.search.bar.demo'),
          show: true,
        },
      },
    }),
  },
  social: {
    getDefaultValue: () => ({
      show: true,
      facebook: true,
      twitter: true,
      linkedin: true,
      copyQuestionLink: true,
    }),
    metadata: {
      type: DataType.schema,
      defaultValue: {
        type: 'static',
        value: {
          show: true,
          facebook: true,
          twitter: true,
          linkedin: true,
          copyQuestionLink: true,
        },
      },
    },
  },
  openOneAnswer: {
    getDefaultValue: () => true,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  openQuestionIcon: {
    inheritFromAppScope: true,
    getDefaultValue: () => OpenQuestionIcon.ARROW,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: OpenQuestionIcon.EYEICON,
      },
      mobileDefaultValue: {
        type: 'static',
        value: OpenQuestionIcon.ARROW,
      },
    },
  },
  openQuestionIconURL: {
    getDefaultValue: () => '',
    metadata: {
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: '',
      },
    },
  },
  dividerCollapse: {
    getDefaultValue: () => 1,
    metadata: {
      type: DataType.number,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },

  mobile: {
    getDefaultValue: () => ({
      titleFontSize: 20,
      categoryFontSize: 16,
      questionsNumberFontSize: 50,
      questionsFontSize: 16,
      answersFontSize: 14,
    }),
    metadata: {
      type: DataType.schema,
      defaultValue: {
        type: 'static',
        value: {
          titleFontSize: 20,
          categoryFontSize: 16,
          questionsNumberFontSize: 50,
          questionsFontSize: 16,
          answersFontSize: 14,
        },
      },
    },
    inheritDesktop: true,
    inheritFromAppScope: true,
  },
  previewFirstAnswer: {
    getDefaultValue: () => false,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
    inheritFromAppScope: true,
    inheritDesktop: false,
  },
  categoriesPosition: {
    inheritDesktop: false,
    getDefaultValue: () => CategoriesPosition.TOP,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: CategoriesPosition.TOP,
      },
    },
  },
  structure: {
    inheritDesktop: false,
    getDefaultValue: ({ isMobile }: { isMobile: boolean }) =>
      Structure.ONE_COLUMN,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: Structure.ONE_COLUMN,
      },
      mobileDefaultValue: {
        type: 'static',
        value: Structure.ONE_COLUMN,
      },
    },
  },
  spacing: {
    type: SettingsParamType.Object,
    key: 'spacing',
    getDefaultValue: getSpacingDefaults,
    metadata: {
      type: DataType.schema,
      defaultValue: {
        type: 'static',

        fallback: {
          gap: 12,
          betweencols: 12,
          vertical: 32,
          horizontal: 32,
        },
      },
      mobileDefaultValue: {
        type: 'static',
        fallback: {
          gap: 12,
          betweencols: 12,
          vertical: 20,
          horizontal: 20,
        },
      },
    },
    inheritDesktop: true,
  },
  itemAppearance: {
    getDefaultValue: ({ isMobile }: { isMobile: boolean }) =>
      isMobile ? ItemAppearance.LINE : ItemAppearance.BOX,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: ItemAppearance.BOX,
      },
      mobileDefaultValue: {
        type: 'static',
        value: ItemAppearance.LINE,
      },
    },
  },
  titleText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) => t('display.tab.title.demo'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('display.tab.title.demo'),
      },
    }),
  },
  searchBarText: {
    type: SettingsParamType.Text,
    getDefaultValue: ({ t }) => t('display.tab.search.bar.demo'),
    metadata: ({ t }) => ({
      type: DataType.text,
      defaultValue: {
        type: 'static',
        value: t('display.tab.search.bar.demo'),
      },
    }),
  },
  isEditor3: {
    getDefaultValue: () => false,
    metadata: {
      type: DataType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
});
export default settingsParams;
